import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  AlertCircle,
  BarChart3,
  Loader2,
  Printer,
  RefreshCw,
} from "lucide-react";

import {
  AnalysisReport,
  EMPTY_FILTERS,
  FilterBar,
  MONTH_TO_NUM,
  printRef,
  type AnalysisFilters,
} from "../components/ApplicationAnalysisReport";
import {
  useGetActiveUserCategoriesQuery,
  useGetApplicationsAnalysisQuery,
  useGetChaptersQuery,
  useGetSectorsQuery,
  useGetStatesQuery,
} from "../api/probationerApplicationApi";
import {
  useGetDietsQuery,
  type LogbookDiet,
} from "../../logbook-diets/api/logbookDietApi";

/**
 * Application Analysis — the standalone screen.
 *
 * The same report the diet and category screens open in a modal, but reached
 * from the sidebar and starting UNSCOPED: every application the institute has
 * ever taken. The diet and category are two more filters here rather than the
 * fixed frame they are in the modal, so a question that spans intakes
 * ("how many female applicants have we ever had?") can actually be asked.
 */
export default function AdminApplicationAnalysisPage() {
  const printContentRef = useRef<HTMLDivElement>(null);

  // Scope: both optional. Empty means "everything".
  const [dietId, setDietId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [filters, setFilters] = useState<AnalysisFilters>(EMPTY_FILTERS);

  const { data: diets = [] } = useGetDietsQuery();
  const { data: categories = [] } = useGetActiveUserCategoriesQuery();
  const { data: states = [] } = useGetStatesQuery();
  const { data: chapters = [] } = useGetChaptersQuery();
  const { data: sectors = [] } = useGetSectorsQuery();

  const sortedDiets = useMemo(
    () =>
      [...diets].sort((a: LogbookDiet, b: LogbookDiet) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      }),
    [diets],
  );

  const dietLabel = dietId
    ? sortedDiets.find((d: LogbookDiet) => String(d.id) === dietId)?.title ?? "Selected Diet"
    : "All Diets";
  const categoryLabel = categoryId
    ? categories.find((c) => String(c.id) === categoryId)?.name ?? "Selected Category"
    : "All Categories";

  const reportTitle = !dietId && !categoryId
    ? "Institute-Wide Analysis — All Diets"
    : `${categoryLabel} — ${dietLabel}`;

  const { data, isLoading, isFetching, error, refetch } =
    useGetApplicationsAnalysisQuery({
      dietId: dietId || undefined,
      categoryId: categoryId ? Number(categoryId) : undefined,
      status: filters.status || undefined,
      year: filters.year || undefined,
      // The backend's whereMonth() wants 1-12, the filter bar shows names.
      month: filters.month ? MONTH_TO_NUM[filters.month] : undefined,
      age_range: filters.age_range || undefined,
      gender: filters.gender || undefined,
      whois: filters.whois || undefined,
      state_id: filters.state_id || undefined,
      chapter_id: filters.chapter_id || undefined,
      sector_id: filters.sector_id || undefined,
    });

  const activeCount = Object.values(filters).filter(Boolean).length;
  const scopedCount = (dietId ? 1 : 0) + (categoryId ? 1 : 0);

  const handlePrint = () => printRef(printContentRef.current, reportTitle);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3 no-print">
        <div>
          <Link
            to="/probationer-applications/admin"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Browse Applications
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-xl font-black tracking-tight text-gray-900">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Application Analysis
          </h1>
          <p className="text-xs text-gray-500 font-medium mt-0.5">
            {reportTitle}
            {activeCount > 0 && (
              <span className="text-indigo-600 font-bold">
                {" "}
                · {activeCount} filter{activeCount > 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh"
            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 text-gray-600 ${isFetching ? "animate-spin" : ""}`}
            />
          </button>
          <button
            onClick={handlePrint}
            disabled={isLoading || !!error}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest hover:bg-indigo-500 disabled:opacity-50 transition-colors"
          >
            <Printer className="w-4 h-4" /> Print Report
          </button>
        </div>
      </div>

      {/* ── Scope + filters ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden no-print">
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-gray-100">
          <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 shrink-0">
            Scope
          </span>

          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 shrink-0">
              Diet
            </span>
            <select
              value={dietId}
              onChange={(e) => setDietId(e.target.value)}
              className={`text-[11px] font-semibold border rounded-lg px-2.5 py-1.5 outline-none transition-all cursor-pointer ${
                dietId
                  ? "border-indigo-400 text-indigo-700 bg-indigo-50"
                  : "border-gray-200 text-gray-600 bg-white hover:border-gray-400"
              }`}
            >
              <option value="">All Diets</option>
              {sortedDiets.map((d: LogbookDiet) => (
                <option key={d.id} value={String(d.id)}>
                  {d.title}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 shrink-0">
              Category
            </span>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className={`text-[11px] font-semibold border rounded-lg px-2.5 py-1.5 outline-none transition-all cursor-pointer ${
                categoryId
                  ? "border-indigo-400 text-indigo-700 bg-indigo-50"
                  : "border-gray-200 text-gray-600 bg-white hover:border-gray-400"
              }`}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          {scopedCount > 0 && (
            <button
              onClick={() => {
                setDietId("");
                setCategoryId("");
              }}
              className="ml-auto px-3 py-1.5 rounded-lg bg-gray-100 border border-gray-200 text-gray-600 text-[10px] font-black uppercase tracking-wider hover:bg-gray-200 transition-all"
            >
              Reset scope
            </button>
          )}
        </div>

        <FilterBar
          filters={filters}
          onChange={setFilters}
          states={states}
          chapters={chapters}
          sectors={sectors}
        />
      </div>

      {/* ── Report ── */}
      <div className="rounded-2xl border border-gray-200 bg-white px-6 py-6 md:px-8">
        {isFetching && !isLoading && (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-100 no-print">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
            <span className="text-[11px] font-semibold text-indigo-600">
              Applying filters…
            </span>
          </div>
        )}

        {isLoading ? (
          <div className="py-32 flex flex-col items-center gap-4 text-gray-400">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
            <p className="text-sm font-semibold">Generating analysis report…</p>
            <p className="text-xs text-gray-300">
              Aggregating application data across all fields
            </p>
          </div>
        ) : error ? (
          <div className="py-24 text-center space-y-5">
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-lg">
                Could not load analysis
              </p>
              <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                {(error as any)?.data?.message ||
                  "The server could not generate the report. Please try again."}
              </p>
            </div>
            <button
              onClick={() => refetch()}
              className="px-6 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div ref={printContentRef}>
            <AnalysisReport data={data} title={reportTitle} filters={filters} />
          </div>
        )}
      </div>
    </div>
  );
}
